import { filterTopupsByStates } from '@/src/api/payg';
import { PAYG_EVENTS } from '@/src/constants/analytics';
import AnalyticsService from '@/src/services/analyticsService';
import React, { useEffect, useMemo } from 'react';
import {
  CouldNotAuthorisePayment,
  FinalNotifications,
  ProcessingNotification,
} from './components';
import { usePaygRouteParams, usePendingTopups } from './utils';

export const PaygNotifications = () => {
  const { topupSessionId, hasSuccessfulPayment, clearRouteParams } =
    usePaygRouteParams();

  const topups = usePendingTopups({ topupSessionId });

  const unpaidAlert = useMemo(() => {
    if (!topupSessionId) {
      return undefined;
    }
    return filterTopupsByStates(topups || [], ['PENDING_PAYMENT']).find(
      topup => topup.topupSessionId === topupSessionId,
    );
  }, [topups, topupSessionId]);

  const noUnpaidAlerts = topups != null && unpaidAlert == null;

  useEffect(() => {
    if (noUnpaidAlerts) {
      clearRouteParams();
    }
  }, [noUnpaidAlerts, clearRouteParams]);

  const isFailedAlert = unpaidAlert && !hasSuccessfulPayment;

  const processingAlerts = useMemo(() => {
    const allAlerts = () => {
      const base = filterTopupsByStates(topups || [], [
        'PROCESSING',
        'AUTO_APPLY_DELAYED', // Maybe we need a separate case for it?
      ]);
      if (isFailedAlert || !unpaidAlert) {
        return base;
      }
      // The unpaid alert is deliberately last because it is always the newest one
      return [...base, unpaidAlert];
    };
    return allAlerts();
  }, [topups, isFailedAlert, unpaidAlert]);

  const finalisedAlerts = useMemo(
    () =>
      filterTopupsByStates(topups || [], [
        'CONTACT_US',
        'CONTACT_US_APPLY_FAILED',
        'SUCCESSFULLY_APPLIED',
        'MANUALLY_ENTER_UTRN',
        'TRY_AGAIN_LATER',
      ]),
    [topups],
  );

  useEffect(() => {
    if (!topupSessionId) {
      return;
    }
    if (hasSuccessfulPayment) {
      AnalyticsService.dispatch(
        ...PAYG_EVENTS.postPaymentApproved({ topupSessionId }),
      );
    } else {
      AnalyticsService.dispatch(
        ...PAYG_EVENTS.postPaymentFailed({ topupSessionId }),
      );
    }
  }, [hasSuccessfulPayment, topupSessionId]);

  return (
    <>
      {processingAlerts.map(alert => (
        <ProcessingNotification topup={alert} key={alert.topupSessionId} />
      ))}
      {topups && <FinalNotifications topups={finalisedAlerts} />}
      {isFailedAlert && (
        <CouldNotAuthorisePayment onDismiss={clearRouteParams} />
      )}
    </>
  );
};
